/**
 * useSearchReport — TanStack Query hook for the Search Detail report.
 *
 * Polls while the backend reports `generating`, using the interval the RESPONSE
 * carries (`refresh_after_seconds`) rather than a constant of our own: the server
 * knows how long a compose takes and can change its mind without a frontend
 * release.
 *
 * Three things stop the loop, and all three matter:
 *
 * - a `refresh_after_seconds` of 0. That is the server saying asking again cannot
 *   change the answer: the report is fresh, or nothing it controls is going to
 *   start a compose. It is deliberately the interval and not the `status`,
 *   because `unavailable` covers both a queue that is not configured (terminal —
 *   retrying every few seconds would hammer the endpoint over something the user
 *   cannot fix) and a dispatch that errored, which is retryable and says so with
 *   a real interval.
 * - a TERMINAL error (404). It never becomes a 200 by asking again, and one
 *   arriving mid-poll used to leave the page issuing a 404 per interval
 *   indefinitely while still showing the figures of a search that had left the
 *   mirror.
 * - the poll BUDGET. A DLQ'd compose worker never answers, and an open tab would
 *   otherwise ask forever under a caption promising "a few seconds". After
 *   `MAX_POLL_ATTEMPTS` the hook reports `pollingExhausted` and the page offers a
 *   manual retry, which is the honest version of the same behaviour.
 *
 * Unmounting also stops it: TanStack tears the interval down with the observer,
 * which the hook's test pins so nobody has to trust that from memory.
 */

import type { SearchReportResponse } from "@nu-assist/contracts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { useRef } from "react";

import {
  SearchNotFoundError,
  type SearchReportClient,
} from "../../../clients/searchReport.client";

/** Fallback cadence when the payload omits an interval (older API). */
const FALLBACK_POLL_MS = 3000;

/**
 * Answered polls allowed per visit before the loop gives up.
 *
 * A budget in REQUESTS rather than in seconds, because it now governs two
 * cadences: 40 at the server's 3s compose cadence is ~2 minutes, far longer than
 * a compose takes (~10s) and far shorter than the 15-minute claim TTL; 40 at the
 * 10s dispatch-retry cadence is ~6.7 minutes, which is an ordinary length for an
 * SQS incident and is meant to outlast one. Either way an exhaustion means
 * something is genuinely wrong (a DLQ'd job, a worker that cannot start, a queue
 * that is not answering) rather than something slow.
 */
export const MAX_POLL_ATTEMPTS = 40;

export interface UseSearchReportArgs {
  client: SearchReportClient;
  /** Thrive search id from the route. Empty disables the query entirely. */
  searchId: string;
}

export interface UseSearchReportResult {
  query: UseQueryResult<SearchReportResponse>;
  /**
   * The budget ran out while the payload was still asking to be polled.
   *
   * Both polling states, not just `generating`: a dispatch-failed `unavailable`
   * polls too, and it needs the same honest ending.
   */
  pollingExhausted: boolean;
  /** Manual exit from any stalled state: restores the budget and asks again. */
  retry: () => void;
}

/**
 * Whether this failure can ever be resolved by asking again.
 *
 * A 404 means the search is not in the mirror, which no amount of polling
 * changes. It must stop the loop AND escalate past the "couldn't refresh just
 * now" banner, because that copy tells the reader the figures below are merely
 * stale when in fact they describe a search that is gone.
 *
 * A predicate rather than three `instanceof` checks at the three call sites
 * (`retry`, `refetchInterval`, the page's error branch) — those would drift, and
 * a stop rule that disagrees with the escalation rule is how a page ends up
 * silently polling a terminal error.
 */
export function isTerminalReportError(error: unknown): boolean {
  return error instanceof SearchNotFoundError;
}

/**
 * Milliseconds until the next poll, or `false` to stop.
 *
 * Exported for its own unit test: this is the only piece of the hook with real
 * branching, and driving TanStack's timers in a test to assert it would be far
 * more fragile than calling the function.
 */
export function pollIntervalFor(
  data: SearchReportResponse | undefined,
): number | false {
  if (!data) return false;
  const seconds = data.refresh_after_seconds;
  // `refresh_after_seconds`, not `status`, is the server's stop signal — and this
  // used to branch on `status === "generating"`, which threw away the only case
  // where the two disagree. `unavailable` covers both a queue that is not
  // configured (terminal, and it answers 0) and a dispatch that ERRORED, which
  // answers a real interval because the next request may well succeed. Reading
  // only the status left a cold search dead after one transient SQS fault.
  if (typeof seconds === "number" && seconds > 0) return seconds * 1000;
  // A `generating` payload from an API that omits the interval still has to be
  // polled; anything else with a 0 means stop.
  return data.status === "generating" ? FALLBACK_POLL_MS : false;
}

/** The composed stop rule: budget and terminal errors both outrank the payload. */
export function nextPollInterval(args: {
  data: SearchReportResponse | undefined;
  error: unknown;
  attempts: number;
  maxAttempts?: number;
}): number | false {
  const max = args.maxAttempts ?? MAX_POLL_ATTEMPTS;
  if (isTerminalReportError(args.error)) return false;
  if (args.attempts >= max) return false;
  return pollIntervalFor(args.data);
}

export function useSearchReport({
  client,
  searchId,
}: UseSearchReportArgs): UseSearchReportResult {
  // Requests spent on THIS visit. A ref rather than TanStack's own counters:
  // those live on the cached query, so they outlive the page and a search visited
  // twice would arrive already over budget. Counted in `queryFn`, so retries
  // count too — the thing worth bounding is calls to the API, not just answers.
  const spent = useRef(0);
  const budgetFor = useRef(searchId);
  // Route moved to another search: give it its own budget. Adjusting a ref during
  // render (rather than in an effect) is the React-documented shape for deriving
  // from changed props without an extra commit.
  if (budgetFor.current !== searchId) {
    budgetFor.current = searchId;
    spent.current = 0;
  }

  const query = useQuery<SearchReportResponse>({
    queryKey: ["searches", "report", searchId],
    queryFn: () => {
      spent.current += 1;
      return client.getReport(searchId);
    },
    enabled: Boolean(searchId),
    refetchInterval: (q) =>
      nextPollInterval({
        data: q.state.data,
        error: q.state.error,
        attempts: spent.current,
      }),
    // Keep the previous payload on screen across polls so the page does not
    // flash between "generating with stale data" and the refreshed report.
    placeholderData: (prev) => prev,
    // A 404 will never become a 200 by asking again. Everything else keeps the
    // default retry.
    retry: (failureCount, error) =>
      !isTerminalReportError(error) && failureCount < 3,
  });

  return {
    query,
    // "The budget stopped a loop that would otherwise still be running", asked of
    // the payload rather than of one status name. This was `status ===
    // "generating"`, which was true of every polling state when it was written
    // and stopped being so the moment a dispatch-failed `unavailable` began
    // polling: `nextPollInterval` ended that loop at the cap while
    // `pollingExhausted` stayed false, so the page kept saying "we're still
    // trying" after it had stopped. Deriving it from the same function the loop
    // uses means a future polling state cannot fall out of it either.
    pollingExhausted:
      spent.current >= MAX_POLL_ATTEMPTS && pollIntervalFor(query.data) !== false,
    retry: () => {
      spent.current = 0;
      void query.refetch();
    },
  };
}
